import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import supabase from '../../supabase/supabase';
import { Button } from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { SRI_LANKA_DISTRICTS, SRI_LANKA_CITIES, ALL_CITIES, getDistrictForCity } from '../../utils/locations';

const LOGO_URL = "https://res.cloudinary.com/dfg8nrgvf/image/upload/q_auto/f_auto/v1780696106/Remove_Background_wepspn.png";
const libraries = ['places'];

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  
  const [customerCity, setCustomerCity] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  
  const [district, setDistrict] = useState('');
  const [serviceCities, setServiceCities] = useState([]);
  const [barberCitySearch, setBarberCitySearch] = useState('');
  const [showBarberCityDropdown, setShowBarberCityDropdown] = useState(false);
  
  const [role, setRole] = useState('customer');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState(null);
  
  const [barberSignupEnabled, setBarberSignupEnabled] = useState(true);
  const [checkingBarberSignup, setCheckingBarberSignup] = useState(false);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries
  });
  
  const [autocomplete, setAutocomplete] = useState(null);
  const onLoad = (autoC) => setAutocomplete(autoC);
  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      if (place.formatted_address) setAddress(place.formatted_address);
    }
  };

  const filteredCustomerCities = ALL_CITIES.filter(c => 
    c.toLowerCase().includes(citySearch.toLowerCase())
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('signup') === 'true') setIsSignUp(true);
    if (searchParams.get('role') === 'barber') {
      setRole('barber');
      setIsSignUp(true);
    }
  }, [location.search]);

  useEffect(() => {
    if (role === 'barber' && isSignUp) {
      const checkBarberSignup = async () => {
        setCheckingBarberSignup(true);
        try {
          const { data } = await supabase
            .from('platform_settings_1780960000000')
            .select('value')
            .eq('key', 'barber_signup_enabled')
            .maybeSingle();
          if (data) setBarberSignupEnabled(data.value === true || data.value === 'true');
          else setBarberSignupEnabled(true);
        } catch {
          setBarberSignupEnabled(true);
        } finally {
          setCheckingBarberSignup(false);
        }
      };
      checkBarberSignup();
    }
  }, [role, isSignUp]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const sanitizedEmail = email.trim().toLowerCase();

    try {
      const { data: existingProfile } = await supabase
        .from('profiles_1740423033529')
        .select('id, role')
        .eq('email', sanitizedEmail)
        .maybeSingle();

      if (isSignUp) {
        if (existingProfile) {
          setError("This email is already registered. Please Sign In instead.");
          setLoading(false);
          setIsSignUp(false);
          return;
        }
        
        if (!phone || phone.length < 9) throw new Error("Please provide a valid phone number.");
        if (role === 'customer' && !customerCity) throw new Error("Please select your city.");
        if (role === 'barber' && !district) throw new Error("Please select your district.");
        if (role === 'barber' && serviceCities.length === 0) throw new Error("Please select at least one service city.");
        if (!acceptedTerms) throw new Error("You must agree to the Terms and Conditions.");
        if (password !== confirmPassword) throw new Error("Passwords do not match.");

        if (role === 'barber') {
          const { data } = await supabase.from('platform_settings_1780960000000').select('value').eq('key', 'barber_signup_enabled').maybeSingle();
          const enabled = data ? (data.value === true || data.value === 'true') : true;
          if (!enabled) throw new Error("Barber registrations are currently closed. Please check back later.");
        }

        const customerDistrict = role === 'customer' ? getDistrictForCity(customerCity) : null;

        const { data, error: authError } = await supabase.auth.signUp({
          email: sanitizedEmail,
          password,
          options: {
            data: {
              full_name: fullName,
              role,
              phone,
              address,
              city: role === 'customer' ? customerCity : null,
              district: role === 'customer' ? customerDistrict : district,
              service_cities: role === 'barber' ? serviceCities : [],
            },
            emailRedirectTo: `https://bookotg.com/verified`
          }
        });

        if (authError) throw authError;

        if (data?.user && data?.session === null) {
          navigate('/verify-email', { state: { email: sanitizedEmail } });
        } else if (data?.session) {
          navigate('/auth/callback');
        }
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: sanitizedEmail,
          password
        });

        if (signInError) {
          if (signInError.message.toLowerCase().includes("invalid login credentials") && existingProfile) {
            setError("Login failed. If you signed up with Google, please click 'Continue with Google' below.");
          } else {
            throw signInError;
          }
        } else {
          navigate('/auth/callback');
        }
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      if (isSignUp && role === 'barber') {
        const { data } = await supabase.from('platform_settings_1780960000000').select('value').eq('key', 'barber_signup_enabled').maybeSingle();
        const enabled = data ? (data.value === true || data.value === 'true') : true;
        if (!enabled) {
          setError("Barber registrations are currently closed. Please check back later.");
          setLoading(false);
          return;
        }
      }

      if (isSignUp) localStorage.setItem('pending_role', role);
      else localStorage.removeItem('pending_role');

      // 1. Get the OAuth URL without automatically redirecting
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `https://bookotg.com/auth/callback`,
          skipBrowserRedirect: true // This prevents the WebView from navigating itself
        }
      });

      if (error) throw error;

      // 2. Open the URL in an external browser using a target="_blank" anchor
      if (data?.url) {
        const link = document.createElement('a');
        link.href = data.url;
        link.target = '_blank'; // This tells the App wrapper to open the system browser (Safari/Chrome)
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Reset loading state after a delay in case user returns manually
        setTimeout(() => setLoading(false), 3000);
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-light flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-8 cursor-pointer" onClick={() => navigate('/')}>
          <img src={LOGO_URL} alt="BookOTG" className="h-24 w-auto object-contain" />
        </div>
        <h2 className="text-center text-3xl font-black text-brand-dark tracking-tight">
          {isSignUp ? 'Join the Elite' : 'Welcome back'}
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md pb-10">
        <div className="bg-white py-10 px-8 shadow-2xl rounded-[2.5rem] border border-gray-100">
          
          {error && (
            <div className="mb-6 p-4 rounded-2xl text-sm border flex items-start gap-3 bg-red-50 text-red-600 border-red-100 animate-in fade-in slide-in-from-top-1">
              <SafeIcon name="AlertCircle" className="mt-0.5 flex-shrink-0" />
              <span className="font-bold">{error}</span>
            </div>
          )}

          {isSignUp && (
            <div className="flex p-1 bg-gray-100 rounded-2xl gap-1 mb-6">
              <button
                type="button"
                onClick={() => setRole('customer')}
                className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  role === 'customer' ? 'bg-white text-brand-primary shadow-sm' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                New Customer
              </button>
              <button
                type="button"
                onClick={() => setRole('barber')}
                className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  role === 'barber' ? 'bg-white text-brand-primary shadow-sm' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                New Barber
              </button>
            </div>
          )}

          {isSignUp && role === 'barber' && !checkingBarberSignup && !barberSignupEnabled && (
            <div className="mb-6 p-5 bg-orange-50 border border-orange-100 rounded-2xl flex items-start gap-3">
              <SafeIcon name="AlertTriangle" className="text-orange-500 flex-shrink-0 mt-0.5 text-xl" />
              <div>
                <p className="font-black text-orange-800 text-sm">Barber Registrations Closed</p>
                <p className="text-xs text-orange-700 font-medium mt-1">New barber sign-ups are temporarily disabled by the admin.</p>
              </div>
            </div>
          )}

          <Button
            type="button"
            variant="outline"
            className="w-full py-4 text-sm font-black rounded-2xl flex items-center justify-center gap-3 hover:bg-gray-50 border-gray-200"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" className="w-5 h-5" />
            Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-4">
            <div className="h-px bg-gray-100 flex-1"></div>
            <span className="text-[10px] font-black tracking-widest uppercase text-gray-400">
              {isSignUp ? 'Or fill up the form' : 'Or sign in with email'}
            </span>
            <div className="h-px bg-gray-100 flex-1"></div>
          </div>

          <form className="space-y-5" onSubmit={handleAuth}>
            {isSignUp && (
              <>
                <input
                  type="text"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-5 py-4 border border-gray-200 rounded-2xl bg-gray-50 outline-none focus:border-brand-primary font-bold"
                  placeholder="Full Name"
                />
                
                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 pl-1">Phone Number *</label>
                  <div className="flex bg-gray-50 border border-gray-200 rounded-2xl focus-within:border-brand-primary overflow-visible transition-colors relative z-50">
                    <PhoneInput
                      defaultCountry="lk"
                      value={phone}
                      onChange={(p) => setPhone(p)}
                      className="w-full flex"
                      inputStyle={{
                        width: '100%',
                        height: '56px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        fontWeight: '700',
                        fontSize: '1rem',
                        outline: 'none',
                        paddingLeft: '12px'
                      }}
                      countrySelectorStyleProps={{
                        buttonStyle: {
                          height: '56px',
                          border: 'none',
                          backgroundColor: 'transparent',
                          paddingLeft: '16px',
                          paddingRight: '8px'
                        }
                      }}
                    />
                  </div>
                </div>

                {role === 'customer' && (
                  <div className="relative">
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 pl-1">Your City *</label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search your city..."
                        value={customerCity || citySearch}
                        onChange={(e) => {
                          setCitySearch(e.target.value);
                          setCustomerCity('');
                          setShowCityDropdown(true);
                        }}
                        onFocus={() => setShowCityDropdown(true)}
                        className="w-full px-5 py-4 border border-gray-200 rounded-2xl bg-gray-50 outline-none focus:border-brand-primary font-bold text-brand-dark"
                      />
                      {customerCity && <div className="absolute right-4 top-1/2 -translate-y-1/2"><SafeIcon name="CheckCircle" className="text-green-500" /></div>}
                    </div>
                    
                    {showCityDropdown && !customerCity && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setShowCityDropdown(false)}></div>
                        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl max-h-60 overflow-y-auto py-2">
                          {filteredCustomerCities.length > 0 ? filteredCustomerCities.map(c => (
                            <div 
                              key={c} 
                              className="px-5 py-3 hover:bg-brand-primary/5 cursor-pointer font-bold text-brand-dark text-sm transition-colors flex justify-between items-center"
                              onClick={() => {
                                setCustomerCity(c);
                                setCitySearch(c);
                                setShowCityDropdown(false);
                              }}
                            >
                              <span>{c}</span>
                              <span className="text-[10px] text-gray-400 font-medium">{getDistrictForCity(c)}</span>
                            </div>
                          )) : <div className="px-5 py-3 text-gray-400 text-sm italic">No cities found</div>}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {role === 'barber' && (
                  <>
                    <select
                      required
                      value={district}
                      onChange={(e) => {
                        setDistrict(e.target.value);
                        setServiceCities([]);
                      }}
                      className="w-full px-5 py-4 border border-gray-200 rounded-2xl bg-gray-50 outline-none focus:border-brand-primary font-bold text-brand-dark appearance-none"
                    >
                      <option value="" disabled>Select your District</option>
                      {SRI_LANKA_DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>

                    {district && (
                      <div className="relative">
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 pl-1">Service Cities in {district} *</label>
                        <div 
                          className="w-full px-5 py-4 border border-gray-200 rounded-2xl bg-gray-50 flex flex-wrap gap-2 cursor-pointer focus-within:border-brand-primary transition-colors min-h-[56px]"
                          onClick={() => setShowBarberCityDropdown(true)}
                        >
                          {serviceCities.map(c => (
                            <span key={c} className="bg-brand-primary text-white text-xs px-3 py-1 rounded-lg font-bold flex items-center gap-1">
                              {c}
                              <div className="ml-1 cursor-pointer hover:text-red-200" onClick={(e) => {
                                e.stopPropagation();
                                setServiceCities(prev => prev.filter(x => x !== c));
                              }}>
                                <SafeIcon name="X" className="text-[10px]" />
                              </div>
                            </span>
                          ))}
                          <input
                            type="text"
                            placeholder={serviceCities.length === 0 ? "Search & Select Cities" : ""}
                            value={barberCitySearch}
                            onChange={e => setBarberCitySearch(e.target.value)}
                            className="bg-transparent outline-none flex-1 min-w-[150px] font-bold text-brand-dark"
                          />
                        </div>

                        {showBarberCityDropdown && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setShowBarberCityDropdown(false)}></div>
                            <div className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl max-h-56 overflow-y-auto py-2">
                              {(SRI_LANKA_CITIES[district] || [])
                                .filter(c => c.toLowerCase().includes(barberCitySearch.toLowerCase()) && !serviceCities.includes(c))
                                .map(c => (
                                <div 
                                  key={c} 
                                  className="px-5 py-3 hover:bg-brand-primary/5 cursor-pointer font-bold text-brand-dark text-sm transition-colors"
                                  onClick={() => {
                                    setServiceCities([...serviceCities, c]);
                                    setBarberCitySearch('');
                                    setShowBarberCityDropdown(false);
                                  }}
                                >
                                  {c}
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}

                {role === 'customer' && isLoaded && (
                  <div className="relative">
                    <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
                      <input
                        type="text"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        className="w-full px-5 py-4 border border-brand-primary/20 rounded-2xl bg-blue-50/30 outline-none focus:border-brand-primary font-bold pr-12"
                        placeholder="Home address (optional)..."
                      />
                    </Autocomplete>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-primary"><SafeIcon name="MapPin" /></div>
                  </div>
                )}
              </>
            )}

            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-5 py-4 border border-gray-200 rounded-2xl bg-gray-50 outline-none focus:border-brand-primary font-bold"
              placeholder="Email Address"
            />
            
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-5 py-4 border border-gray-200 rounded-2xl bg-gray-50 outline-none focus:border-brand-primary font-bold"
              placeholder="Password"
            />

            {isSignUp && (
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-5 py-4 border border-gray-200 rounded-2xl bg-gray-50 outline-none focus:border-brand-primary font-bold"
                placeholder="Confirm Password"
              />
            )}

            {!isSignUp && (
              <div className="flex justify-end px-1">
                <button type="button" onClick={() => navigate('/auth/forgot-password')} className="text-xs font-bold text-brand-primary hover:underline">Forgot Password?</button>
              </div>
            )}

            {isSignUp && (
              <div className="flex items-start gap-3 px-1 mt-2">
                <input 
                  type="checkbox" 
                  id="terms" 
                  required 
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 text-brand-primary rounded border-gray-300 focus:ring-brand-primary" 
                />
                <label htmlFor="terms" className="text-xs font-medium text-gray-500 leading-tight">
                  I agree to the <Link to="/terms" target="_blank" className="text-brand-primary font-bold hover:underline">Terms</Link> and{' '}
                  <Link to="/privacypolicy" target="_blank" className="text-brand-primary font-bold hover:underline">Privacy Policy</Link>.
                </label>
              </div>
            )}

            <Button
              type="submit"
              className="w-full py-5 text-lg font-black rounded-2xl hover:bg-brand-accent transition-colors"
              disabled={loading || (isSignUp && role === 'barber' && !barberSignupEnabled)}
            >
              {loading ? 'Processing...' : isSignUp ? 'Create Account' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-10 pt-8 border-t border-gray-100 text-center">
            <button
              onClick={() => {
                setIsSignUp(!isSignUp);
                setError(null);
              }}
              className="text-sm font-black text-brand-primary"
            >
              {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Join Now"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}